import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";
import { RING_STYLE } from "../constants/rings";

const STATES = [
  { key: "off", label: "Off", hint: "Nobody" },
  { key: "internal", label: "Internal", hint: "Only the internal ring (your test shops)" },
  { key: "early", label: "Early", hint: "Internal and early rings" },
  { key: "percent", label: "Percentage", hint: "Internal, early, and this share of everyone else" },
  { key: "on", label: "Everyone", hint: "All shops" },
];

const SCOPE = {
  dashboard: "bg-indigo-50 text-indigo-700",
  till: "bg-emerald-50 text-emerald-700",
  job: "bg-amber-50 text-amber-700",
  menu: "bg-sky-50 text-sky-700",
};

/** One flag: who has it, with a percentage for a gradual rollout and a switch per shop. */
function FlagCard({ flag, shops, onChanged }) {
  const [state, setState] = useState(flag.state);
  const [percent, setPercent] = useState(flag.percent);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const dirty = state !== flag.state || (state === "percent" && Number(percent) !== flag.percent);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.updateFlag(flag.key, { state, percent: Number(percent) || 0 });
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const override = async (shopId, value) => {
    setBusy(true);
    try {
      await platformAPI.setFlagOverride(flag.key, shopId, value);
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="bg-white rounded-xl shadow p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">
            {flag.description}{" "}
            <span className={`ms-1 text-xs px-2 py-0.5 rounded-full ${SCOPE[flag.scope] || ""}`}>{flag.scope}</span>
          </p>
          <p className="text-xs text-gray-400 font-mono">{flag.key}</p>
        </div>
        <button type="button" onClick={() => setOpen(!open)} className="text-sm text-primary-700 hover:underline">
          Reaches {flag.reaches} of {flag.of} shops {open ? "▲" : "▼"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATES.map((option) => (
          <button
            key={option.key}
            type="button"
            title={option.hint}
            onClick={() => setState(option.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
              state === option.key
                ? option.key === "off"
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-primary-600 text-white border-primary-600"
                : "bg-white text-gray-600 border-gray-200"
            }`}
          >
            {option.label}
          </button>
        ))}
        {state === "percent" && (
          <label className="flex items-center gap-1 text-sm text-gray-600">
            <input
              type="number" min="0" max="100"
              className="w-20 px-2 py-1 border border-gray-300 rounded-lg"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
            />
            % of the general ring
          </label>
        )}
        {dirty && (
          <button
            type="button" disabled={busy} onClick={save}
            className="ms-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-1.5 rounded-lg disabled:opacity-50"
          >
            Save
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {open && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500 mb-2">
            Per shop: force on or off whatever the state says. A forced shop is marked.
          </p>
          <ul className="divide-y divide-gray-50 text-sm">
            {shops.map((shop) => {
              const forced = flag.overrides[shop.id];
              const has = flag.reached_shops.some((r) => r.id === shop.id);

              return (
                <li key={shop.id} className="py-1.5 flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <Link to={`/shops/${shop.id}`} className="text-gray-800 hover:underline">{shop.name}</Link>{" "}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${RING_STYLE[shop.ring]}`}>{shop.ring}</span>{" "}
                    <span className={has ? "text-green-700" : "text-gray-400"}>{has ? "has it" : "does not"}</span>
                    {typeof forced === "boolean" && (
                      <span className="ms-1 text-xs text-amber-700">(forced {forced ? "on" : "off"})</span>
                    )}
                  </span>
                  <span className="flex gap-1">
                    {[["On", true], ["Off", false], ["Follow", null]].map(([label, value]) => (
                      <button
                        key={label} type="button" disabled={busy}
                        onClick={() => override(shop.id, value)}
                        className={`px-2 py-0.5 rounded border text-xs ${
                          (value === null ? typeof forced !== "boolean" : forced === value)
                            ? "bg-gray-800 text-white border-gray-800"
                            : "bg-white text-gray-500 border-gray-200"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </li>
  );
}

/**
 * Staged rollout. Each flag decides who gets a new feature: off, only the internal
 * ring (your test shops), the early ring too, a growing percentage of everyone
 * else, or all. Moving a flag back is instant and needs no deploy: the feature
 * simply disappears for the shops taken out. Change a shop's ring on its page.
 */
export default function Releases() {
  const [flags, setFlags] = useState([]);
  const [shops, setShops] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: shopList }] = await Promise.all([platformAPI.listFlags(), platformAPI.listShops()]);
      setFlags(list);
      setShops(shopList.filter((shop) => shop.is_active));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load releases");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = shops.reduce((sum, shop) => ({ ...sum, [shop.ring]: (sum[shop.ring] || 0) + 1 }), {});

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Releases</h1>
      <p className="text-sm text-gray-500 mb-4">
        Roll a feature out in steps: your own shops first, then the early ring, then a growing share of everyone. Rolling
        back is instant and needs no deploy.
      </p>

      <p className="text-sm text-gray-600 mb-6">
        Rings:{" "}
        {["internal", "early", "general"].map((ring) => (
          <span key={ring} className={`me-2 px-2 py-0.5 rounded-full text-xs font-semibold ${RING_STYLE[ring]}`}>
            {ring} {counts[ring] || 0}
          </span>
        ))}
      </p>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      <ul className="space-y-3">
        {flags.map((flag) => (
          <FlagCard key={`${flag.key}:${flag.state}:${flag.percent}`} flag={flag} shops={shops} onChanged={load} />
        ))}
      </ul>
    </div>
  );
}
